import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  formatCustomerNo,
  formatInvoiceNo,
  parseRecordId,
  settingsPage,
  SETTINGS_ENTRIES,
  SETTINGS_PAGES,
} from '@tenfour/contracts'
import { useAuth, userCan } from '@/components/auth/AuthContext'
import { api } from '@/lib/api'
import { formatCurrency, formatPhone } from '@/lib/utils'

export interface SearchResults {
  customers: Array<{ id: string; customerNumber?: number | null; name: string; phone: string; address?: string }>
  jobs: Array<{ id: string; customerName: string; description: string; status: string }>
  invoices: Array<{ id: string; invoiceNumber: number; customerName: string; total: string; status: string }>
}

export interface PageEntry {
  id: string
  label: string
  sub: string
  href: string
  keywords: string
}

export interface FlatItem {
  type: 'page' | 'customer' | 'job' | 'invoice'
  id: string
  label: string
  sub: string
  href: string
}

/**
 * Destination pages the palette can jump to. Keep the hrefs on the current IA —
 * primary nav routes (`/home`, `/messages`, `/customers-v2`, `/money`) and the
 * real `/settings/*` sub-pages, not the pre-split `/settings` catch-all.
 */
/**
 * Settings destinations come from the shared catalog rather than a second
 * hand-written copy here. Before this, the palette kept its own thirteen
 * settings entries, which drifted from the rail whenever a page moved or was
 * renamed. One list, two consumers: the settings rail and its own search read
 * exactly the same entries (#626-#637).
 */
const SETTINGS_PAGE_ENTRIES: readonly (PageEntry & { ownerOnly?: boolean; adjustOnly?: boolean })[] = SETTINGS_PAGES.map((page) => ({
  id: `settings-${page.id}`,
  label: page.label === 'Account' ? 'Account settings' : page.label,
  sub: page.sub,
  href: page.href,
  keywords: `settings ${page.label.toLowerCase()} ${page.keywords}`,
  ownerOnly: page.ownerOnly,
  // Everything but Security sits behind the canAdjustSettings capability, the
  // same gate the rail applies (#186). Without this a Field Tech's results all
  // bounced off RequireAdjustSettings.
  adjustOnly: !['security', 'account', 'invoices'].includes(page.id),
}))

/**
 * Individual settings, so the palette reaches a single control and not just the
 * page holding it. Typing "overdue" lands on the two overdue rows rather than on
 * Payments, and the row anchor scrolls it into view on arrival.
 */
const SETTINGS_ENTRY_RESULTS: readonly (PageEntry & {
  ownerOnly?: boolean
  adjustOnly?: boolean
})[] = SETTINGS_ENTRIES.map((entry) => {
  const page = settingsPage(entry.page)
  return {
    id: `setting-${entry.page}-${entry.anchor}`,
    label: entry.label,
    sub: `${page?.label ?? 'Settings'}, ${entry.card}`,
    href: `${page?.href ?? '/settings'}#${entry.anchor}`,
    keywords: `settings ${entry.card.toLowerCase()} ${entry.keywords}`,
    // Owner-gated rows do not render for a member, so the anchor would never
    // resolve and they would land on the page with nothing highlighted.
    ownerOnly: page?.ownerOnly || entry.ownerOnly,
    adjustOnly: !['security', 'account', 'invoices'].includes(page?.id ?? ''),
  }
})

const CORE_PAGES: readonly PageEntry[] = [
  { id: 'home', label: 'Home', sub: 'For you, activity, and money at a glance', href: '/home', keywords: 'pulse dashboard home overview activity recap feed stats revenue what we have been up to for you' },
  { id: 'calls', label: 'Calls', sub: 'Phone call history and transcripts', href: '/calls', keywords: 'phone calls voice transcript history' },
  { id: 'queue', label: 'Needs your attention', sub: 'Replies, calls, and decisions waiting on you', href: '/queue', keywords: 'queue attention needs you decisions replies calls waiting todo for you inbox triage' },
  { id: 'messages', label: 'Messages', sub: 'SMS threads with your customers', href: '/messages', keywords: 'conversations messages sms texts chat threads customer history transcript inbox unread' },
  { id: 'customers', label: 'Customers', sub: 'Customer list, CRM, edit, search, export', href: '/customers-v2', keywords: 'customers clients contacts phone number email add edit search filter prospect crm export csv' },
  { id: 'money', label: 'Payments', sub: 'Payments received and payouts to your bank', href: '/money', keywords: 'money invoices billing payments payouts revenue earnings balance charge collect' },
  { id: 'jobs', label: 'Jobs', sub: 'Job backlog, status, detail', href: '/jobs', keywords: 'queue jobs work orders status completed canceled export csv requests schedule' },
  { id: 'forms', label: 'Forms', sub: 'Booking form, custom forms, publish, responses', href: '/forms', keywords: 'forms form builder booking questions publish embed responses collect intake' },
  { id: 'invoices', label: 'Invoices', sub: 'Invoice workspace — index, preview, export', href: '/invoices', keywords: 'invoices billing payments pdf void delete send revenue chart filter export csv quote' },
  { id: 'payouts', label: 'Payouts', sub: 'Stripe balance, bank transfers, earnings', href: '/money?tab=payouts', keywords: 'payments payouts stripe balance bank account transfers deposits earnings connect finances payout' },
  { id: 'meetings', label: 'Meetings', sub: 'Google Meet meetings, transcripts, AI summaries', href: '/calendar/meetings', keywords: 'meetings meet video call transcript summary schedule' },
  { id: 'reminders', label: 'Reminders', sub: 'Personal to-dos, due dates, recurring, text reminders', href: '/reminders', keywords: 'reminders tasks to-do todo due date recurring repeat checklist things to do call follow up personal notes' },
  { id: 'reports', label: 'Reports', sub: 'Earnings, charts, top customers, export payments', href: '/reports', keywords: 'reports analytics earnings revenue charts bar graph top customers export payments csv tax filing' },
  { id: 'notifications-page', label: 'Activity', sub: 'Every notification, newest first, mark read', href: '/notifications', keywords: 'notifications activity alerts messages payment job invoice updates read unread' },
] as const

export const PAGES: readonly PageEntry[] = [
  ...CORE_PAGES,
  ...SETTINGS_PAGE_ENTRIES,
  ...SETTINGS_ENTRY_RESULTS,
]

export const EMPTY_RESULTS: SearchResults = { customers: [], jobs: [], invoices: [] }

/** `initialQuery` seeds the first render (the dialog reopening on the query
 *  that survived closing); later changes go through `setQuery`. */
export function useGlobalSearch({ suggestPages = false, initialQuery = '' }: { suggestPages?: boolean; initialQuery?: string } = {}) {
  const { accountId, user } = useAuth()
  const isOwner = user?.accountRole !== 'MEMBER' && user?.role !== 'MEMBER'
  const canAdjust = userCan(user, 'canAdjustSettings')
  const [query, updateQuery] = useState(initialQuery)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [attempt, setAttempt] = useState(0)
  const [response, setResponse] = useState<{
    key: string
    results: SearchResults
    error: boolean
    loading: boolean
  } | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const normalizedQuery = query.trim()
  // A sequence number such as "2" is a valid search. Free text needs two
  // characters, matching the server's record-ID rule.
  const canSearch = Boolean(accountId && (normalizedQuery.length >= 2 || parseRecordId(normalizedQuery)))
  const requestKey = `${accountId ?? ''}\u0000${normalizedQuery}`
  const currentResponse = canSearch && response?.key === requestKey ? response : null
  // Do not render a previous account/query's records while an effect or the
  // debounce is pending. Query identity owns the visible response.
  const results = currentResponse?.results ?? EMPTY_RESULTS
  const isLoading = canSearch && (!currentResponse || currentResponse.loading)
  const isError = currentResponse?.error ?? false

  const setQuery = useCallback((next: string) => {
    if (next === query) return
    if (next.trim() !== query.trim()) abortRef.current?.abort()
    updateQuery(next)
    setSelectedIndex(0)
  }, [query])

  const retry = useCallback(() => {
    abortRef.current?.abort()
    setResponse(null)
    setAttempt(value => value + 1)
  }, [])

  useEffect(() => {
    if (!canSearch || !accountId) return
    const controller = new AbortController()
    abortRef.current = controller
    setResponse({ key: requestKey, results: EMPTY_RESULTS, error: false, loading: true })
    const timer = setTimeout(async () => {
      try {
        const data = await api<SearchResults>(
          `/search?q=${encodeURIComponent(normalizedQuery)}&accountId=${encodeURIComponent(accountId)}`,
          { signal: controller.signal },
        )
        if (!controller.signal.aborted) {
          setResponse({ key: requestKey, results: data, error: false, loading: false })
        }
      } catch {
        if (!controller.signal.aborted) {
          setResponse({ key: requestKey, results: EMPTY_RESULTS, error: true, loading: false })
        }
      }
    }, 200)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [accountId, normalizedQuery, canSearch, requestKey, attempt])

  const matchedPages = useMemo(() => {
    if (normalizedQuery.length < 2 && !(suggestPages && normalizedQuery.length === 0)) return [] as PageEntry[]
    const q = normalizedQuery.toLowerCase()
    return PAGES.filter((p) => {
      if (p.href === '/reports' && !userCan(user, 'canViewReports')) return false
      const gated = p as { ownerOnly?: boolean; adjustOnly?: boolean }
      if (gated.ownerOnly && !isOwner) return false
      if (gated.adjustOnly && !canAdjust) return false
      return (
        p.label.toLowerCase().includes(q) ||
        p.keywords.includes(q) ||
        p.sub.toLowerCase().includes(q)
      )
    }).slice(0, normalizedQuery ? 5 : 10)
  }, [normalizedQuery, isOwner, canAdjust, suggestPages, user])

  const { allItems, indexMap } = useMemo(() => {
    // Row copy is sentence case (design system copy rule); the API's enum
    // statuses (ACTIVE, PENDING_REVIEW) read as words here.
    const sentence = (value: string) =>
      value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase().replace(/_/g, ' ') : value
    const items: FlatItem[] = []
    for (const p of matchedPages) {
      items.push({ type: 'page', id: p.id, label: p.label, sub: p.sub, href: p.href })
    }
    for (const c of results.customers) {
      const cusNo = c.customerNumber == null ? '' : `${formatCustomerNo(c.customerNumber)} · `
      items.push({ type: 'customer', id: c.id, label: c.name, sub: cusNo + formatPhone(c.phone) + (c.address ? ` · ${c.address}` : ''), href: `/customers-v2/${c.id}` })
    }
    for (const j of results.jobs) {
      items.push({ type: 'job', id: j.id, label: j.customerName, sub: `${j.description} · ${sentence(String(j.status))}`, href: `/jobs/${j.id}` })
    }
    for (const i of results.invoices) {
      items.push({ type: 'invoice', id: i.id, label: `${formatInvoiceNo(i.invoiceNumber, { quote: String(i.status).toUpperCase() === 'QUOTE' })} - ${i.customerName}`, sub: `${formatCurrency(Number(i.total))} · ${sentence(String(i.status))}`, href: `/invoices/${i.id}` })
    }
    const map = new Map<string, number>()
    items.forEach((item, idx) => map.set(`${item.type}:${item.id}`, idx))
    return { allItems: items, indexMap: map }
  }, [matchedPages, results])

  const getIndex = useCallback(
    (type: FlatItem['type'], id: string) => indexMap.get(`${type}:${id}`) ?? -1,
    [indexMap],
  )

  const reset = useCallback(() => {
    updateQuery('')
    setResponse(null)
    setSelectedIndex(0)
    abortRef.current?.abort()
  }, [])

  return {
    query,
    setQuery,
    results,
    canSearch,
    isLoading,
    isError,
    retry,
    matchedPages,
    allItems,
    indexMap,
    getIndex,
    selectedIndex,
    setSelectedIndex,
    reset,
  }
}
